import { AssetType, SourceType } from "@nendaTypes/cms/CmsAssetType";
import { buildCmsAssetsQuery } from "@client/http/cmsAssetQuery";

export const VOD_TYPE_OPTIONS: { value: AssetType; label: string }[] = [
  { value: AssetType.Movie, label: "Movie" },
  { value: AssetType.Series, label: "Series" },
  { value: AssetType.Season, label: "Season" },
  { value: AssetType.Episode, label: "Episode" },
];

export const SOURCE_TYPE_OPTIONS: { value: SourceType; label: string }[] = [
  { value: SourceType.vod, label: "VOD" },
  { value: SourceType.live, label: "Live" },
  { value: SourceType.catchup, label: "Catchup" },
  { value: SourceType.deeplink, label: "Deeplink" },
];

export const PAGE_SIZE_OPTIONS = [10, 25, 50];
export const DEFAULT_PAGE_SIZE = 25;

export interface VODFilters {
  type: AssetType[];
  sourceType: SourceType[];
}

export const EMPTY_FILTERS: VODFilters = { type: [], sourceType: [] };

export interface VodListParams {
  q: string;
  filters: VODFilters;
  page: number;
  pageSize: number;
}

const pickAllowed = <T extends string>(
  values: string[],
  options: { value: T }[]
): T[] => {
  const allowed = options.map((o) => o.value);
  return [...new Set(values)].filter((v): v is T => allowed.includes(v as T));
};

export const parseVodListParams = (params: URLSearchParams): VodListParams => {
  const page = Number.parseInt(params.get("page") ?? "", 10);
  const pageSize = Number.parseInt(params.get("pageSize") ?? "", 10);

  return {
    q: params.get("q")?.trim() ?? "",
    filters: {
      type: pickAllowed(params.getAll("type"), VOD_TYPE_OPTIONS),
      sourceType: pickAllowed(params.getAll("sourceType"), SOURCE_TYPE_OPTIONS),
    },
    page: Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: PAGE_SIZE_OPTIONS.includes(pageSize)
      ? pageSize
      : DEFAULT_PAGE_SIZE,
  };
};

export const toVodListSearchParams = ({
  q,
  filters,
  page,
  pageSize,
}: VodListParams): URLSearchParams => {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  filters.type.forEach((t) => params.append("type", t));
  filters.sourceType.forEach((s) => params.append("sourceType", s));
  if (page > 1) params.set("page", String(page));
  if (pageSize !== DEFAULT_PAGE_SIZE) params.set("pageSize", String(pageSize));
  return params;
};

export const buildVodAssetsQuery = ({
  q,
  filters,
  page,
  pageSize,
}: VodListParams): string =>
  buildCmsAssetsQuery({
    page,
    pageSize,
    originalTitle: q,
    type: filters.type,
    sourceType: filters.sourceType,
  });
